import React, { useEffect, useMemo, useRef } from "react";
import { FixedSizeList } from "react-window";
import { Text } from "@merge-api/merge-javascript-shared";
import { APICategory, UnreleasedAPICategory } from "../../../../../../../../models/Entities";
import { LIST_GUTTER_CLASS, MAX_VISIBLE_ROWS, OVERSCAN, ROW_HEIGHT } from "../constants";
import { SyncSelection } from "../types";
import SyncSelectionsListRow, { SyncSelectionsListData } from "./SyncSelectionsListRow";

interface Props {
  category: APICategory | UnreleasedAPICategory;
  selections: SyncSelection[];
  totalCount: number;
}

const SyncSelectionsList = ({ category, selections, totalCount }: Props) => {
  // hooks
  const listRef = useRef<FixedSizeList<SyncSelectionsListData>>(null);

  // derived state
  // Sized from the unfiltered count so narrowing the search never resizes the card and shifts the page.
  const height = Math.min(totalCount, MAX_VISIBLE_ROWS) * ROW_HEIGHT;
  const itemData = useMemo(() => ({ category, selections }), [category, selections]);

  // effects
  // A new query swaps out the rows under the viewport, so the results start from the top.
  useEffect(() => {
    listRef.current?.scrollTo(0);
  }, [selections]);

  // render
  return (
    <div className="relative">
      <FixedSizeList
        ref={listRef}
        className={LIST_GUTTER_CLASS}
        height={height}
        width="100%"
        itemCount={selections.length}
        itemSize={ROW_HEIGHT}
        itemData={itemData}
        itemKey={(index, data) => data.selections[index].id}
        overscanCount={OVERSCAN}
      >
        {SyncSelectionsListRow}
      </FixedSizeList>
      {selections.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Text className="text-gray-40">No selections match your search</Text>
        </div>
      )}
    </div>
  );
};

export default SyncSelectionsList;
